import { uniqBy, sortBy } from 'lodash';
import { IShadow, ISpace } from '../../types/space';
import { ElementTypes } from '../../types/element';
import { getShadowCopies } from './helper';
import { getSpaceId } from '../../helpers';

const COLLISION_BUFFER = 5;

export const getSpacesToKageNoBunshinNoJitsu = (spacesById: {
  [key: string]: ISpace<ElementTypes>;
}) => {
  // 1. Get all the spaces we need to adjust
  const spacesAvoidingCollision = Object.values(spacesById).filter(
    (space) => space.isAvoidCollision,
  );
  // 2. Organize them depending on position.
  const sortedSpaces = sortBy(spacesAvoidingCollision, ['posY']);

  // 3. Grab shadows clones. We can mutate these directly.
  const shadowClones = getShadowCopies(spacesById);

  return [sortedSpaces, shadowClones];
};

export const getAllConflictResolvedShadowClones = ({
  sortedSpaces,
  shadowClones,
  printDepth,
}: {
  sortedSpaces: ISpace<ElementTypes>[];
  shadowClones: { [key: string]: IShadow };
  printDepth?: number;
}) => {
  const documentEl = document;

  return sortedSpaces.reduce((shadowAccum, realSpace) => {
    // 1. Find the element to grab the height first.
    const spaceEl = documentEl.getElementById(getSpaceId(realSpace.id, printDepth));

    if (!spaceEl) return shadowAccum;
    // 2. Update the shadow height. heightBuffer is some weird shit just for handling text line-height
    const heightBuffer = calculateShadowHeightBuffer(realSpace, spaceEl.offsetHeight);
    shadowAccum[realSpace.id].height = spaceEl.offsetHeight + heightBuffer;

    // 3. Handle all the collisions
    return _collectResolvedCollisions(shadowAccum[realSpace.id], shadowAccum);
  }, shadowClones);
};

export const _collectResolvedCollisions = (
  dynamicSpace: IShadow,
  accum: { [key: string]: IShadow },
) => {
  /**
   * Evaluates a single dynamic space for all its collisions, and resolves them.
   */
  // 1st collect all the collisions
  const collisionsIds = _getAllCollisions(dynamicSpace, accum) as string[];

  // If no collisions, stop function.
  if (collisionsIds.length === 0) return accum;

  // 2nd find the collision that is has the highest posY
  const highestCollisionSpaceId = collisionsIds.reduce((prevId, currentId) =>
    accum[prevId].posY < accum[currentId].posY ? prevId : currentId,
  );

  // 3rd find the distance we need to shift all spaces below
  const dynamicSpaceBottomPos = dynamicSpace.posY + dynamicSpace.height;
  const distanceToShift =
    dynamicSpaceBottomPos - accum[highestCollisionSpaceId].posY + COLLISION_BUFFER;

  // 4th shift all spaces below by the distance
  return _getShiftSpacesBelow(collisionsIds, distanceToShift, dynamicSpace, accum);
};

const _getShiftSpacesBelow = (
  collisionsIds: string[],
  distanceToShift: number,
  spaceRoot: IShadow,
  accum: { [key: string]: IShadow },
) => {
  /**
   * @collisionsIds The space Ids that have collided
   * @distanceToShift The amount of pixels to shift spaces down
   * @spaceRoot All spaces below this space should move down
   * We need to combine all the collided spaces, and all the spaces below. Then shift all of them
   * down by the distanceToShift which was calculated by taking the highest colliding space
   */
  const spacesToShift = _getSpacesDirectlyUnderneath(spaceRoot, accum).concat(collisionsIds);

  // TODO @Derek This will shift all spaces below, no matter what position to shift down.
  uniqBy(spacesToShift, (id) => id).forEach((id) => {
    if (id === spaceRoot.id) return; // Don't move itself
    const spaceToShift = accum[id];
    accum[spaceToShift.id].posY = spaceToShift.posY + distanceToShift;
  });

  return accum;
};

/** Comparison helpers */
/**
 * Find if specific space is colliding with any other space
 */
const _getAllCollisions = (space: IShadow, byId: { [key: string]: IShadow }) => {
  return Object.keys(byId).filter((id: string) => {
    if (id === space.id) return false;
    return _collides(byId[id], space);
  });
};

/**
 * This helps us find all the spaces underneath, not on the right or left side, but any
 * that touches any space directly underneath.
 */
const _getSpacesDirectlyUnderneath = (s2: IShadow, accum: { [key: string]: IShadow }) => {
  const spacesToShift = Object.keys(accum).filter((id) => {
    const s1 = accum[id];
    if (s1.posY < s2.posY) return false; // Ignore anything above.
    if (s1.posX + s1.width + COLLISION_BUFFER <= s2.posX) return false; // s1 is left of s2
    if (s1.posX >= s2.posX + s2.width + COLLISION_BUFFER) return false; // s1 is right of s2
    return true;
  });

  return spacesToShift;
};

/**
 * Given two layout items, check if they collide.
 */
export const _collides = (s1: IShadow, s2: IShadow) => {
  if (s1 === s2) return false; // same element
  if (s1.posX + s1.width + COLLISION_BUFFER <= s2.posX) return false; // s1 is left of s2
  if (s1.posX >= s2.posX + s2.width + COLLISION_BUFFER) return false; // s1 is right of s2
  if (s1.posY + s1.height + COLLISION_BUFFER <= s2.posY) return false; // s1 is above s2
  if (s1.posY >= s2.posY + s2.height + COLLISION_BUFFER) return false; // s1 is below s2
  return true; // spaces overlap
};

/**
 * I really hate doing this but something weird happens when you have text that has line-height.
 * It kinda cuts off the top and button because of the way browsers behave naturally with line-height.
 * Because of this, we need to make the height for the space taller than what we read from finding the span.
 *
 * https://stackoverflow.com/questions/4089379/align-block-elements-on-top-when-using-line-height
 */
export const calculateShadowHeightBuffer = (space: ISpace<ElementTypes>, innerHeight: number) => {
  if (space.type === ElementTypes.FieldLongText || space.type === ElementTypes.FieldText) {
    try {
      const numPageGapsPassed = getNumPageGapsPassed(innerHeight, space.posY);
      const fontSize = (space as ISpace<ElementTypes.FieldText>).config.fontSize || 0;
      const lineHeight = Number((space as ISpace<ElementTypes.FieldText>).config.lineHeight);

      return (fontSize * lineHeight - fontSize) / 2 + numPageGapsPassed * (lineHeight * fontSize);
    } catch (e) {
      return 0;
    }
  }

  return 0;
};

/**
 * Some other janky and weird code. Basically a multiline text when in printing mode, if it passes over the gaps,
 * it will actually auto give it a line break... *sometimes*. So we will actually put an estimate here on how many
 * of these page gaps will pass and give it some height.
 *
 * If we don't do this, when you print it, theres a large chance
 * that the bottom will be cut off. This happens when the multiline text crosses over the page gaps.
 *
 * @param elHeight
 * @param elPosY
 */
const ESTIMATED_PAGE_HEIGHT = 1000;
const getNumPageGapsPassed = (elHeight: number, elPosY: number) => {
  const pageGapsTotal = Math.floor((elHeight + elPosY) / ESTIMATED_PAGE_HEIGHT);
  const pageGapsAlreadyPassed = Math.floor(elPosY / ESTIMATED_PAGE_HEIGHT);

  return pageGapsTotal - pageGapsAlreadyPassed > 0 ? pageGapsTotal - pageGapsAlreadyPassed : 0;
};
